import React, { CSSProperties, ReactNode } from 'react';
import { useOpenAIGlobal } from 'react-openai-apps-sdk';

interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

interface SafeAreaContainerProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  /**
   * Base height when not in fullscreen mode
   */
  fallbackHeight?: number;
  /**
   * Which insets to apply. Default: all sides
   */
  applyInsets?: {
    top?: boolean;
    bottom?: boolean;
    left?: boolean;
    right?: boolean;
  };
}

/**
 * Container that respects safe area insets (notches, system UI, chat input, etc.)
 * Automatically adjusts height/width based on display mode and safe area constraints.
 *
 * @example
 * ```tsx
 * <SafeAreaContainer fallbackHeight={600} className="bg-background">
 *   <YourWidget />
 * </SafeAreaContainer>
 * ```
 */
export function SafeArea({
                           children,
                           className = '',
                           style = {},
                           fallbackHeight = 600,
                           applyInsets = {top: true, bottom: true, left: true, right: true},
                         }: SafeAreaContainerProps) {
  const maxHeight = useOpenAIGlobal('maxHeight') as number | undefined;
  const displayMode = useOpenAIGlobal('displayMode') as string | undefined;
  const safeArea = useOpenAIGlobal('safeArea') as { insets: SafeAreaInsets } | undefined;

  const insets = safeArea?.insets || {top: 0, bottom: 0, left: 0, right: 0};

  // Calculate insets to apply
  const topInset = applyInsets.top !== false ? insets.top : 0;
  const bottomInset = applyInsets.bottom !== false ? insets.bottom : 0;
  const leftInset = applyInsets.left !== false ? insets.left : 0;
  const rightInset = applyInsets.right !== false ? insets.right : 0;

  // Calculate effective dimensions
  const isFullscreen = displayMode === 'fullscreen';

  // In fullscreen: use calc(100% - bottom inset) for responsive height
  // Top inset should be padding (to push content down), not reduce height
  // In inline: use fixed pixel height minus bottom inset only
  const heightStyle = isFullscreen
    ? `calc(100% - ${bottomInset}px)`
    : `${fallbackHeight - bottomInset}px`;

  return (
    <div
      className={className}
      style={{
        ...style,
        height: heightStyle,
        paddingTop: topInset > 0 ? `${topInset}px` : undefined,
        paddingLeft: leftInset > 0 ? `${leftInset}px` : undefined,
        paddingRight: rightInset > 0 ? `${rightInset}px` : undefined,
      }}
    >
      {children}
    </div>
  );
}
